// Global, promise-based confirm dialog — singleton reactive state + a single
// <ConfirmModal /> mounted once in App.vue (mirrors the job-form-modal.ts
// pattern). Replaces native confirm() everywhere in the app so every
// confirmation dialog looks the same (colorful gradient header, icon, no
// browser chrome) instead of an unstyled OS-native popup.
import { reactive } from 'vue';

export type ConfirmVariant = 'default' | 'danger' | 'warning' | 'info';
export type ConfirmChoice = 'confirm' | 'cancel' | 'extra';

interface ConfirmModalState {
  show: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  extraText: string; // empty = no 3rd button
  variant: ConfirmVariant;
  resolve: ((choice: ConfirmChoice) => void) | null;
}

export const confirmModal = reactive<ConfirmModalState>({
  show: false,
  title: '',
  message: '',
  confirmText: 'ยืนยัน',
  cancelText: 'ยกเลิก',
  extraText: '',
  variant: 'default',
  resolve: null,
});

export interface ConfirmChoiceOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  extraText?: string;
  variant?: ConfirmVariant;
}

/** Full 3-way dialog (confirm / extra / cancel) — for choices with a real middle option. */
export function showConfirmChoice(options: ConfirmChoiceOptions): Promise<ConfirmChoice> {
  return new Promise((resolve) => {
    confirmModal.title = options.title || 'ยืนยัน';
    confirmModal.message = options.message || '';
    confirmModal.confirmText = options.confirmText || 'ยืนยัน';
    // '' (explicit) hides the cancel button — used by showAlertModal.
    confirmModal.cancelText = options.cancelText ?? 'ยกเลิก';
    confirmModal.extraText = options.extraText || '';
    confirmModal.variant = options.variant || 'default';
    confirmModal.resolve = resolve;
    confirmModal.show = true;
  });
}

/** Styled replacement for `alert()` — a single acknowledge button, no cancel. */
export async function showAlertModal(
  options: Omit<ConfirmChoiceOptions, 'extraText' | 'cancelText'>,
): Promise<void> {
  await showConfirmChoice({
    ...options,
    confirmText: options.confirmText || 'ตกลง',
    cancelText: '',
  });
}

/** Drop-in replacement for `confirm()` — resolves true only on the primary button. */
export async function showConfirmModal(
  options: Omit<ConfirmChoiceOptions, 'extraText'>,
): Promise<boolean> {
  const choice = await showConfirmChoice(options);
  return choice === 'confirm';
}

export function confirmModalPick(choice: ConfirmChoice): void {
  const resolve = confirmModal.resolve;
  confirmModal.show = false;
  confirmModal.resolve = null;
  resolve?.(choice);
}
